import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import styles from "./enquirylist.module.css";
import { TextField, Table, TableCell, TableHead, TableContainer, Paper, TableBody, TableRow, TableFooter, InputAdornment, IconButton } from "@mui/material";
import { SearchOutlined, Inventory2Outlined, ClearRounded } from "@mui/icons-material";
import { StyledMainTableCell, StyledMainTableRow } from "../../../components/TableComponent";
import TablePaginationComponent from "../../../components/TablePagination";
import Loader from "../../../components/Loader";
import { authData, withAdminAuth } from "../../../components/GetAuth";
import axios from "axios";
import PageHeader from "../../../components/PageHeader";
import EnquiryListLoader from "./EnquiryListLoader";
import { useWebsite } from "../../../../context/WebsiteContext";
import { useSiteBranding } from "../../../../hooks/useSiteBranding";

const EnquiryList = () => {
  const auth = authData();
  const API_TOKEN = auth?.api_token;
  const { bookingId } = useParams();
  const [loading, setLoading] = useState(false);
  const [contentLoading, setContentLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allData, setAllData] = useState([]);
  const [searchKey, setSearchKey] = useState("");

  const { websiteData } = useWebsite();
  useSiteBranding("Booking enquiry list", websiteData);

  const money = (value = 0) => `£${Number(value).toFixed(2)}`;

  const handleChangePage = (_, newPage) => {
    setPage(newPage);
  };

  const handleRowsPerPage = (event) => {
    setRowsPerPage(Number(event.target.value));
    setPage(0);
  };

  const fetchEnquiries = async () => {
    setContentLoading(true);

    try {
      const { data } = await axios.get(`${process.env.REACT_APP_API_URL}v1/bookings-enquery/${bookingId}`, {
        headers: {
          Authorization: `Bearer ${API_TOKEN}`,
        }
      });

      setAllData(data?.data || []);
    } catch (err) {
      console.error(err);
    } finally {
      setContentLoading(false);
    }
  };

  useEffect(() => {
    fetchEnquiries();
  }, [bookingId]);

  const filteredData = useMemo(() => {
    if (!searchKey.trim()) return allData;

    const key = searchKey.toLowerCase();

    return allData.filter((item) => {
      return (item?.item_id?.toLowerCase().includes(key) || item?.product_title?.toLowerCase().includes(key) || item?.booking_status?.booking_id?.toLowerCase().includes(key));
    });
  }, [allData, searchKey]);

  const handleClearSearch = () => {
    setSearchKey("");
    setPage(0);
  };

  return (<>
    <Loader open={loading} setOpen={setLoading} />

    <div className={styles.MainDiv}>
      <PageHeader title="Bookings" trail={[{ label: "Enquiry List" }]} />

      <div className={styles.MainCard}>
        {contentLoading && <EnquiryListLoader />}

        {!contentLoading && (<>
          <div className={styles.FilterBar}>
            <div className={styles.SearchBox}>
              <TextField
                fullWidth
                placeholder="Search booking, item or product..."
                value={searchKey}
                onChange={(e) => { setSearchKey(e.target.value); setPage(0); }}
                InputProps={{
                  startAdornment: (<InputAdornment position="start"><SearchOutlined /></InputAdornment>),
                  endAdornment: searchKey && (
                    <InputAdornment position="end">
                      <IconButton size="small" aria-label="Clear search" onClick={handleClearSearch}>
                        <ClearRounded fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              {searchKey && (
                <span className={styles.ResultCount}>
                  {filteredData.length} {filteredData.length === 1 ? "result" : "results"}
                </span>
              )}
            </div>
          </div>

          <div className={styles.TableCard}>
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledMainTableCell>Booking ID</StyledMainTableCell>
                    <StyledMainTableCell>Item ID</StyledMainTableCell>
                    <StyledMainTableCell>Product</StyledMainTableCell>
                    <StyledMainTableCell align="right">Paid</StyledMainTableCell>
                    <StyledMainTableCell>Status</StyledMainTableCell>
                    <StyledMainTableCell>Pickup Driver</StyledMainTableCell>
                    <StyledMainTableCell>Delivery Driver</StyledMainTableCell>
                    <StyledMainTableCell>Recipient</StyledMainTableCell>
                    <StyledMainTableCell>Address</StyledMainTableCell>
                    <StyledMainTableCell>Phone</StyledMainTableCell>
                    <StyledMainTableCell>Container</StyledMainTableCell>
                  </TableRow>
                </TableHead>

                <TableBody>
                  {filteredData.length > 0 ? (filteredData.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (<StyledMainTableRow key={item.id || item.item_id} hover>
                    <TableCell data-label="Booking ID"><strong>{item?.booking_status?.booking_id}</strong></TableCell>
                    <TableCell data-label="Item ID">{item?.item_id}</TableCell>
                    <TableCell data-label="Product">
                      <div className={styles.ProductCell}>
                        <span className={styles.ProductTitle}>{item?.product_title}</span>
                        {item?.description && (<small>{item.description}</small>)}
                      </div>
                    </TableCell>
                    <TableCell align="right" data-label="Paid">{money(item?.booking_status?.paid_amount)}</TableCell>
                    <TableCell data-label="Status">
                      <span className={`${styles.StatusBadge} ${styles[item?.status_text?.replace(/\s+/g, "")?.toLowerCase()] || ""}`}>{item?.status_text}</span>
                    </TableCell>
                    <TableCell data-label="Pickup Driver">{item?.driver?.name || "—"}</TableCell>
                    <TableCell data-label="Delivery Driver">{item?.delivery_driver?.name || "—"}</TableCell>
                    <TableCell data-label="Recipient">{item?.recepient_data?.name || "—"}</TableCell>
                    <TableCell data-label="Address">
                      <div className={styles.AddressCell}>{item?.recepient_data?.address || "—"}</div>
                    </TableCell>
                    <TableCell data-label="Phone">{item?.recepient_data?.phone || "—"}</TableCell>
                    <TableCell data-label="Container">{item?.container?.container_no || "—"}</TableCell>
                  </StyledMainTableRow>))) : (<TableRow>
                    <TableCell align="center" colSpan={11} className={styles.EmptyState}>
                      <Inventory2Outlined sx={{ fontSize: 60, opacity: .25, marginBottom: 1 }} />
                      <h3>No enquiries found</h3>
                      <p>No records matched your search.</p>
                    </TableCell>
                  </TableRow>)}
                </TableBody>

                {filteredData.length > 0 && (<TableFooter>
                  <TableRow>
                    <TablePaginationComponent
                      count={filteredData.length}
                      page={page}
                      rowsPerPage={rowsPerPage}
                      colSpan={11}
                      handleChangePage={handleChangePage}
                      handleChangeRowsPerPage={
                        handleRowsPerPage
                      }
                    />
                  </TableRow>
                </TableFooter>)}
              </Table>
            </TableContainer>
          </div>
        </>)}
      </div>
    </div>
  </>);
};

export default withAdminAuth(EnquiryList);